/**
 * /_authed/tenants/$slug — tenant-scoped layout route.
 *
 * This is NOT a page. It's a pathless wrapper that:
 *
 *   1. Validates `$slug` exists.
 *   2. Prefetches the tenant detail via `beforeLoad` so every child
 *      page reads from a warm cache.
 *   3. Throws a NotFoundError (→ tenant-specific 404 UI below) when
 *      the tenant doesn't exist — stale bookmark, deleted tenant,
 *      mistyped slug, or cross-environment URL copy.
 *   4. Passes the freshly-fetched tenant into the child route
 *      context, so children can read `Route.useRouteContext().tenant`
 *      without re-querying.
 *
 * The file name `$slug.tsx` (not `$slug/index.tsx`) makes this a
 * layout route in TanStack Router. A sibling file
 * `$slug/index.tsx` provides the actual landing page.
 */

import {
  createFileRoute,
  Link,
  Outlet,
  notFound,
  useParams,
} from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { Button } from "@pureid/ui";
import { NotFoundError } from "@/lib/api-client";
import type { z } from "zod";
import type { managedTenantContextSchema } from "@/lib/managed-contract";
import { tenantContextOptions } from "@/features/managed-administration/queries";
import { TenantScopeLayout } from "@/features/managed-administration/components/tenant-scope-layout";

interface TenantContext {
  tenant: z.infer<typeof managedTenantContextSchema>["tenant"];
  authority: z.infer<typeof managedTenantContextSchema>;
}

export const Route = createFileRoute("/_authed/tenants/$slug")({
  beforeLoad: async ({ context, params }): Promise<TenantContext> => {
    try {
      const authority = await context.queryClient.fetchQuery(tenantContextOptions(params.slug));
      return { tenant: authority.tenant, authority };
    } catch (err) {
      if (err instanceof NotFoundError) throw notFound();
      throw err;
    }
  },
  component: TenantLayout,
  // Tenant-specific 404 boundary. Fires when the slug resolves
  // to no tenant at all — a stale bookmark after tenant delete,
  // a mistyped slug, or a URL copy-pasted from a different
  // environment. The root notFoundComponent is generic; this
  // one tells the operator exactly what happened and offers a
  // direct path back to the tenant list.
  notFoundComponent: TenantNotFound,
});

function TenantLayout() {
  return <TenantScopeLayout><Outlet /></TenantScopeLayout>;
}

function TenantNotFound() {
  const { slug } = useParams({ from: "/_authed/tenants/$slug" });
  return (
    <div className="max-w-xl mx-auto py-24 px-6 text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-warning/10 border border-warning/30 flex items-center justify-center mb-6">
        <AlertTriangle className="h-6 w-6 text-warning" />
      </div>
      <h1 className="text-xl font-semibold text-foreground mb-2">
        Customer unavailable
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        The customer <code className="font-mono text-foreground">{slug}</code>{" "}
        is unavailable to your account. Check your current assignments in the provider workspace.
      </p>
      <Button asChild variant="outline">
        <Link to="/managed">
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to provider workspace
        </Link>
      </Button>
    </div>
  );
}
